import React, { useState } from 'react';
import { Mail, Inbox, Users, Shield, Zap, Building2, CheckCircle2, ChevronDown, ArrowRight, Cloud, FileText, UserPlus, BookUser, ShieldCheck, MessageSquare, AtSign, Upload, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import BRAND from '@/brand';
import Wordmark from '@/components/Wordmark';

function HentpostLanding() {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  const features = [
    {
      icon: <Inbox size={40} className="text-brand-500" />,
      title: 'Centraliseret indbakke',
      description: 'Al digital post fra Virk og E-Boks samlet ét sted — ingen manuelle logins'
    },
    {
      icon: <Mail size={40} className="text-emerald-500" />,
      title: 'Automatisk videresendelse',
      description: 'Send post videre til kolleger, revisor eller bogholder automatisk og øjeblikkeligt'
    },
    {
      icon: <Users size={40} className="text-highlight-500" />,
      title: 'Multi-virksomhed',
      description: 'Administrer digital post for flere CVR-numre fra ét samlet dashboard'
    },
    {
      icon: <Shield size={40} className="text-amber-500" />,
      title: 'GDPR-sikker',
      description: 'EU-hostede servere, TLS/SSL kryptering og fuld GDPR-compliance'
    }
  ];

  const steps = [
    { step: '01', title: 'Opret konto', description: 'Registrér dig med dit CVR-nummer — tager under 2 minutter' },
    { step: '02', title: 'Tilslut digital post', description: 'Vi forbinder automatisk til Virk og E-Boks' },
    { step: '03', title: 'Modtag post på email', description: 'Al digital post lander direkte i din indbakke fra nu af' },
  ];

  const pricingTiers = [
    {
      name: 'Solo',
      price: { monthly: 59, yearly: 47 },
      companies: '1 selskab',
      features: [
        'Al digital post samlet ét sted',
        'Virk og e-Boks',
        'Automatisk videresendelse',
        'Adressebog og kontakter',
        'Manuelt bilag-upload',
        'Avanceret søgning og PDF-visning',
        'Op til 3 modtagere',
        'GDPR-kompatibel',
        'Support på dansk',
      ],
      cta: 'Vælg Solo',
      tier: 'solo',
      popular: false,
      tag: '1 selskab',
    },
    {
      name: 'Holding',
      price: { monthly: 149, yearly: 119 },
      companies: 'Op til 3 selskaber',
      features: [
        'Alt i Solo',
        'Holding + 2 driftsselskaber',
        'Op til 5 modtagere',
        `Egen bilags-email (cvrXXXXXXXX@${BRAND.mailDomain})`,
        'Daglig SMS-opsummering',
        'Inviter team uden MitID',
        'Godkendelses-flow på videresendelse',
        'Ingen binding',
      ],
      cta: 'Vælg Holding',
      tier: 'holding',
      popular: true,
      tag: 'Op til 3 selskaber',
    },
    {
      name: 'Pro',
      price: { monthly: 349, yearly: 279 },
      companies: '10 selskaber inkluderet',
      features: [
        'Alt i Holding',
        '10 CVR-numre inkluderet — derefter 29,- pr. selskab pr. md.',
        'Ubegrænsede modtagere',
        'Cloud-integration: OneDrive, Google Drive, Dropbox',
        'Videresendelse til e-conomic',
        'Prioriteret support',
      ],
      cta: 'Vælg Pro',
      tier: 'pro',
      popular: false,
      tag: '10 selskaber + 29,- pr. ekstra',
    },
  ];

  return (
    <div className="min-h-screen bg-th-body">
      {/* Navigation */}
      <nav className="always-dark border-b border-[#1e293b] bg-[#111827] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 sm:gap-3">
            <Mail size={28} className="text-brand-500 sm:w-8 sm:h-8" />
            <span className="text-lg sm:text-xl font-heading font-bold text-th-text"><Wordmark /></span>
          </div>
          <div className="flex gap-2 sm:gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/login')}
              className="text-th-text-secondary hover:text-th-text hover:bg-th-hover"
            >
              Log ind
            </Button>
            <Button
              size="sm"
              onClick={() => navigate('/register')}
              className="bg-btn hover:bg-btn-hover text-btn-fg"
            >
              Kom i gang
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-900/10 via-transparent to-highlight-900/10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <Badge className="mb-6 bg-brand-500/10 text-brand-400 border-brand-500/20">
              Digital post gjort enkelt
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight text-th-text mb-6">
              Al din digitale post<br />
              <span className="text-brand-500">direkte i indbakken</span>
            </h1>
            <p className="text-lg sm:text-xl text-th-text-secondary leading-relaxed mb-8 max-w-2xl mx-auto">
              Stop med at logge ind på Virk og E-Boks hver dag. {BRAND.name} samler al din virksomheds
              digitale post og sender det direkte til din email.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => navigate('/register')}
                className="bg-btn hover:bg-btn-hover text-btn-fg text-base sm:text-lg px-6 sm:px-8"
              >
                Prøv gratis i 14 dage
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                className="border-th-border-light text-th-text-secondary hover:bg-th-hover hover:text-th-text text-base sm:text-lg px-6 sm:px-8"
              >
                Se priser
              </Button>
            </div>
          </motion.div>
        </div>
        <div className="flex justify-center pb-12">
          <ChevronDown size={32} className="text-th-text-faint animate-bounce" />
        </div>
      </section>

      {/* Features */}
      <section className="py-12 sm:py-16 lg:py-24 bg-th-sidebar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-th-text mb-4">
              Alt du har brug for
            </h2>
            <p className="text-lg text-th-text-secondary max-w-2xl mx-auto">
              Spar tid og undgå at overse vigtig post fra det offentlige
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-6 bg-th-input border-th-border h-full hover:border-th-border-light transition-colors">
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-heading font-semibold text-th-text mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-th-text-secondary leading-relaxed">
                    {feature.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sådan virker det */}
      <section className="py-12 sm:py-16 lg:py-24 bg-th-body">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-th-text mb-4">
              Op og køre på 5 minutter
            </h2>
            <p className="text-lg text-th-text-secondary">
              Ingen teknisk viden påkrævet
            </p>
          </div>
          <div className="space-y-8">
            {steps.map((s, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.15 }}
                viewport={{ once: true }}
                className="flex items-start gap-6"
              >
                <div className="shrink-0 w-14 h-14 rounded-full bg-brand-600/10 border border-brand-500/20 flex items-center justify-center">
                  <span className="text-brand-400 font-heading font-bold text-sm">{s.step}</span>
                </div>
                <div className="pt-3 flex-1">
                  <h3 className="text-xl font-heading font-semibold text-th-text mb-1">{s.title}</h3>
                  <p className="text-th-text-secondary">{s.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="flex justify-center mt-12">
            <Button
              size="lg"
              onClick={() => navigate('/register')}
              className="bg-btn hover:bg-btn-hover text-btn-fg text-base sm:text-lg px-8"
            >
              Kom i gang
            </Button>
          </div>
        </div>
      </section>

      {/* Integrationer */}
      <section className="py-12 sm:py-16 lg:py-24 bg-th-sidebar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12 lg:mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-th-text mb-4">
              Send dokumenter dér hvor du arbejder
            </h2>
            <p className="text-lg text-th-text-secondary max-w-2xl mx-auto">
              Forbind {BRAND.name} med dine eksisterende værktøjer og lad automatiseringen klare resten.
              Ingen manuel download, ingen vedhæftede mails.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'OneDrive',     description: 'Microsoft 365 cloud-arkivering',          Icon: Cloud,    iconColor: 'text-brand-500',    badge: 'Pro' },
              { name: 'Google Drive', description: 'Automatisk mappestruktur',                Icon: Cloud,    iconColor: 'text-yellow-500',  badge: 'Pro' },
              { name: 'Dropbox',      description: 'Synkroniseret arkiv',                     Icon: Cloud,    iconColor: 'text-sky-500',     badge: 'Pro' },
              { name: 'e-conomic',    description: 'Direkte bilagsoverførsel til bogføring',  Icon: FileText, iconColor: 'text-emerald-500', badge: 'Pro' },
            ].map((integration, idx) => {
              const { Icon } = integration;
              return (
                <motion.div
                  key={integration.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="relative h-full p-6 bg-th-input border border-th-border hover:border-th-border-light transition-colors duration-200">
                    {integration.badge && (
                      <Badge className="absolute top-4 right-4 bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                        {integration.badge}
                      </Badge>
                    )}
                    <div className="flex flex-col items-start gap-4">
                      <Icon size={40} className={integration.iconColor} />
                      <div>
                        <h3 className="font-heading text-xl font-semibold text-th-text mb-2">{integration.name}</h3>
                        <p className="text-sm text-th-text-secondary">{integration.description}</p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-10 text-center"
          >
            <p className="inline-flex items-center gap-2 text-sm sm:text-base text-th-text-muted">
              <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0" />
              Aktivér på 2 minutter under Profil → Cloud-integration. Dokumenter uploades automatisk til din valgte mappe når ny post ankommer.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Bygget til teams */}
      <section className="bg-th-body py-12 sm:py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12 lg:mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-th-text mb-4">
              Hele teamet får adgang — uden MitID
            </h2>
            <p className="text-base sm:text-lg text-th-text-secondary max-w-3xl mx-auto">
              Inviter revisor, bogholder eller kollega på under et minut. Hver person får sin egen login med email og password.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[
              { icon: UserPlus,    iconColor: 'text-brand-500',    title: 'Inviter med ét klik', description: 'Send invite på email. Modtager validerer med SMS-kode og opretter selv password. Ingen MitID nødvendig.' },
              { icon: BookUser,    iconColor: 'text-highlight-500',  title: 'Delt adressebog',     description: 'Gem modtagere af videresendt post én gang. Alle i teamet kan bruge dem til hurtig videresendelse.' },
              { icon: ShieldCheck, iconColor: 'text-emerald-500', title: 'Godkendelses-flow',   description: 'Vigtige videresendelser kan kræve manuel godkendelse fra ejer før de afsendes. Fuld kontrol over hvem der ser hvad.' },
            ].map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full p-6 bg-th-input border border-th-border hover:border-th-border-light transition-colors">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-th-hover flex items-center justify-center">
                        <Icon className={`w-6 h-6 ${feature.iconColor}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-lg font-semibold text-th-text mb-2">{feature.title}</h3>
                        <p className="text-sm text-th-text-secondary leading-relaxed">{feature.description}</p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <Card className="relative p-6 sm:p-8 bg-th-input border border-th-border hover:border-th-border-light transition-colors overflow-hidden">
              <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-sky-500 to-brand-500" />
              <div className="absolute top-4 right-4">
                <Badge className="bg-brand-500/10 text-brand-400 border border-brand-500/20 hover:bg-brand-500/10">
                  Holding &amp; Pro
                </Badge>
              </div>
              <div className="flex flex-col sm:flex-row items-start gap-5 pr-24 sm:pr-32">
                <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-sky-500/10 flex items-center justify-center">
                  <MessageSquare size={32} className="text-sky-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-xl sm:text-2xl font-semibold text-th-text mb-2">
                    Daglig SMS-opsummering
                  </h3>
                  <p className="text-sm sm:text-base text-th-text-secondary leading-relaxed">
                    Få en daglig SMS med antal ulæste beskeder og hvem de er fra. Aldrig miss en deadline igen.
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Hvem er det til */}
      <section className="py-12 sm:py-16 lg:py-24 bg-th-sidebar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-th-text mb-4">
              Perfekt til
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              {
                icon: <Building2 size={32} className="text-brand-500" />,
                title: 'Virksomheder',
                desc: 'Aldrig mere manglende post fra SKAT, Erhvervsstyrelsen eller kommunen'
              },
              {
                icon: <Users size={32} className="text-emerald-500" />,
                title: 'Revisorer',
                desc: 'Håndtér digital post for alle dine klienters CVR-numre fra ét sted'
              },
              {
                icon: <Zap size={32} className="text-amber-500" />,
                title: 'Bogholdergrupper',
                desc: 'Del post automatisk med hele teamet — ingen MitID krævet'
              },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-6 bg-th-input border-th-border text-center hover:border-th-border-light transition-colors">
                  <div className="flex justify-center mb-4">{item.icon}</div>
                  <h3 className="text-xl font-heading font-semibold text-th-text mb-2">{item.title}</h3>
                  <p className="text-th-text-secondary">{item.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Også med i pakken */}
      <section className="bg-th-body py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-th-text mb-2">
              Også med i pakken
            </h2>
            <p className="text-sm text-th-text-secondary">
              Detaljer der gør hverdagen lettere
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: AtSign,   iconColor: 'text-brand-400',    title: 'Egen bilags-email',     description: 'Dedikeret email-adresse hvor kvitteringer fra leverandører automatisk lander.', badge: 'Holding+' },
              { icon: Upload,   iconColor: 'text-emerald-400', title: 'Manuelt bilag-upload',  description: 'Foto eller PDF? Træk papir-kvitteringer direkte ind i indbakken.' },
              { icon: Search,   iconColor: 'text-amber-400',   title: 'Avanceret søgning',     description: 'Find post på afsender, dato, CVR eller status. Aldrig grav efter et dokument igen.' },
              { icon: FileText, iconColor: 'text-highlight-400',  title: 'PDF-visning i browser', description: 'Læs dokumenter direkte uden download. Print eller gem som du vil.' },
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                >
                  <Card className="relative p-5 h-full bg-th-input border-th-border hover:border-th-border-light transition-colors">
                    {item.badge && (
                      <Badge className="absolute top-3 right-3 bg-purple-500/10 text-purple-400 border-0 text-xs px-2 py-0.5">
                        {item.badge}
                      </Badge>
                    )}
                    <Icon size={28} className={`${item.iconColor} mb-3`} />
                    <h3 className="font-heading text-base font-semibold text-th-text mb-2">{item.title}</h3>
                    <p className="text-sm text-th-text-secondary leading-relaxed">{item.description}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-12 sm:py-16 lg:py-24 bg-th-sidebar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-th-text mb-4">
              Vælg den rigtige plan
            </h2>
            <p className="text-lg text-th-text-secondary mb-8">
              Gennemsigtige priser uden skjulte gebyrer
            </p>
            <div className="inline-flex items-center gap-3 bg-th-input p-1 rounded-lg border border-th-border">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${
                  billingPeriod === 'monthly' ? 'bg-brand-600 text-white' : 'text-th-text-secondary hover:text-th-text'
                }`}
              >
                Månedlig
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${
                  billingPeriod === 'yearly' ? 'bg-brand-600 text-white' : 'text-th-text-secondary hover:text-th-text'
                }`}
              >
                Årlig
                <span className="ml-2 text-xs text-emerald-400">Spar 20%</span>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingTiers.map((tier, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className={`p-8 h-full flex flex-col ${
                  tier.popular
                    ? 'bg-th-card border-brand-500/30 ring-2 ring-brand-500/10 shadow-lg'
                    : 'bg-th-input border-th-border'
                }`}>
                  <Badge className={`mb-4 w-fit ${tier.popular ? 'bg-brand-500 text-white' : 'bg-th-hover text-th-text-secondary'}`}>
                    {tier.tag}
                  </Badge>
                  <h3 className="text-2xl font-heading font-bold text-th-text mb-2">{tier.name}</h3>
                  <div className="mb-2">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-th-text">{tier.price[billingPeriod]}</span>
                      <span className="text-th-text-secondary">kr/md</span>
                    </div>
                    <p className="text-sm text-th-text-muted mt-1">
                      {billingPeriod === 'yearly' ? 'faktureres årligt · ' : ''}ekskl. moms
                    </p>
                  </div>
                  <ul className="space-y-3 mb-8 flex-grow">
                    {tier.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-th-text-secondary">
                        <CheckCircle2 size={20} className="text-emerald-500 shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => navigate('/register')}
                    className={`w-full ${
                      tier.popular
                        ? 'bg-btn hover:bg-btn-hover text-btn-fg'
                        : 'bg-th-hover hover:bg-th-hover text-th-text'
                    }`}
                  >
                    {tier.cta}
                  </Button>
                </Card>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-sm text-th-text-muted mt-6">Ingen binding · 14 dages gratis prøve · Intet kreditkort påkrævet</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 lg:py-24 bg-th-body">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-th-text mb-4">
            Klar til at spare tid?
          </h2>
          <p className="text-lg text-th-text-secondary mb-8">
            Kom i gang på 5 minutter og modtag al din digitale post direkte i din indbakke
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/register')}
            className="bg-btn hover:bg-btn-hover text-btn-fg text-lg px-8"
          >
            Start gratis i dag
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-th-border py-8 bg-th-body">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center text-th-text-muted text-sm sm:text-base">
          <p>&copy; 2026 {BRAND.copyrightName}. Alle rettigheder forbeholdes.</p>
          <p className="mt-1 text-sm">{BRAND.ownerLine}</p>
        </div>
      </footer>
    </div>
  );
}

export default HentpostLanding;
